/**
 * Entry point: builds the scene, starts the render loop, mounts the HUD.
 *
 * Per-frame work stays out of React (spec §9.1, CLAUDE.md style rules): the render
 * loop writes smoothed statistics into the Zustand store a few times a second, and
 * React re-renders only on those updates.
 */

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { Vector3 } from 'three';
import { OrbitController } from './input/camera.js';
import { createScene } from './render/renderer.js';
import { generateTestTerrain } from './render/terrain.js';
import { useGameStore } from './state/store.js';
import { Hud } from './ui/Hud.js';
import './style.css';
import type { FromWorker } from './worker/sim.worker.js';

/** How often renderer statistics reach the HUD (ms). */
const STATS_INTERVAL_MS = 250;

function mountHud(): void {
  const hudRoot = document.querySelector('#hud');
  if (hudRoot === null) throw new Error('missing #hud element');
  createRoot(hudRoot).render(
    <StrictMode>
      <Hud />
    </StrictMode>,
  );
}

function startRenderLoop(container: HTMLElement): () => void {
  const field = generateTestTerrain({ n: 256, seed: 1337 });
  const handle = createScene(container, field);
  const controller = new OrbitController(handle.camera, container, new Vector3(0, field.minM, 0));

  // Declared before resize(), which runs immediately and requests a draw.
  let needsRender = true;
  const requestRender = (): void => {
    needsRender = true;
  };

  const resize = (): void => {
    handle.resize(container.clientWidth, container.clientHeight);
    requestRender();
  };
  resize();
  window.addEventListener('resize', resize);

  useGameStore.getState().setRenderStats({
    backend: handle.backend,
    vertexCount: handle.vertexCount,
  });

  let last = performance.now();
  let statsAt = last;
  let frames = 0;
  let frameMsTotal = 0;
  let running = true;

  const frame = (now: number): void => {
    if (!running) return;
    const dtS = (now - last) / 1000;
    last = now;

    const cameraMoved = controller.update(dtS);
    if (cameraMoved || needsRender) {
      needsRender = false;
      const startedAt = performance.now();
      handle.renderer.render(handle.scene, handle.camera);
      frames += 1;
      frameMsTotal += performance.now() - startedAt;
    }

    // Report only over frames actually drawn. When the scene is static the loop
    // idles and the panel keeps its last real measurement rather than decaying
    // towards a zero that would not mean anything.
    if (now - statsAt >= STATS_INTERVAL_MS && frames > 0) {
      const windowS = (now - statsAt) / 1000;
      useGameStore.getState().setRenderStats({
        fps: Math.round(frames / windowS),
        frameMs: frameMsTotal / frames,
        altitudeM: Math.round(controller.altitudeM),
        tiltDeg: Math.round(controller.tiltDeg),
      });
      statsAt = now;
      frames = 0;
      frameMsTotal = 0;
    } else if (now - statsAt >= STATS_INTERVAL_MS) {
      statsAt = now;
    }
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);

  // The page must be cross-origin isolated for SharedArrayBuffer; say so loudly in
  // the console if the headers are missing, because the M1 worker depends on it.
  const worker = new Worker(new URL('./worker/sim.worker.ts', import.meta.url), { type: 'module' });
  worker.addEventListener('message', (event: MessageEvent<FromWorker>) => {
    const { crossOriginIsolated: isolated, hardwareConcurrency } = event.data;
    const detail = `${hardwareConcurrency} hardware threads`;
    if (isolated) {
      console.info(`sim worker ready, cross-origin isolated, ${detail}`);
    } else {
      console.warn(
        `sim worker ready but the page is NOT cross-origin isolated (${detail}); ` +
          'SharedArrayBuffer is unavailable, so check the COOP/COEP headers in vite.config.ts',
      );
    }
  });

  return () => {
    running = false;
    window.removeEventListener('resize', resize);
    controller.dispose();
    worker.terminate();
    handle.dispose();
  };
}

const viewport = document.querySelector<HTMLElement>('#viewport');
if (viewport === null) throw new Error('missing #viewport element');
mountHud();
startRenderLoop(viewport);
